import { TENOR_COL_ID } from '@protos/grids';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { GetRowIdParams, ITooltipParams } from 'ag-grid-enterprise';
import { useEffect, useMemo } from 'react';
import { Grid } from '../../grid';
import { usePricesSettings } from '../PricesContext';
import { PricesRow } from '../types';
import { usePricesGrid } from '../usePricesGrid';
import { PricesAnalyticsTooltip } from './PricesAnalyticsTooltip';

function getRowId({ data: { tenorId } }: GetRowIdParams<PricesRow>): string {
  return tenorId;
}

export default function PricesGrid() {
  const { columnDefs, columns, rows, filteredSelectedTenors, isShowCurrencyUnit, onColumnMoved, gridRef, productMap, tenorMap, onColumnResized } =
    usePricesSettings();
  const user = useUserContext();

  const {
    getRowStyle,
    getContextMenuItems,
    onGridCellClicked,
    isExternalFilterPresent,
    doesExternalFilterPass,
    getAllRows,
    selectedRowIds,
    onContextMenuVisibleChange,
  } = usePricesGrid(rows ?? [], filteredSelectedTenors);

  const enhancePriceColumns = (children: any[], colId: string) => {
    return children.map(child => {
      if (child.children?.length) {
        return {
          ...child,
          children: enhancePriceColumns(child.children, colId),
        };
      }

      return {
        ...child,
        ...(colId && user?.cot_product_groups.includes(productMap[colId]?.product_group)
          ? {
              tooltipValueGetter: (p: ITooltipParams) => p.value,
              tooltipComponent: PricesAnalyticsTooltip,
              tooltipComponentParams: { columnId: colId, productMap, tenorMap },
            }
          : {}),
      };
    });
  };

  const priceColumns = useMemo(() => {
    return columns?.reduce((acc, col: any) => {
      if (col.colId !== TENOR_COL_ID) {
        acc.push({
          ...col,
          suppressHeaderContextMenu: true,
          children: col.children ? enhancePriceColumns(col.children, col.colId) : undefined,
        });
      } else {
        acc.push({ ...col, children: [col], suppressHeaderContextMenu: true });
      }
      return acc;
    }, [] as any);
  }, [columns, user, isShowCurrencyUnit, productMap, tenorMap]);

  useEffect(() => {
    // After filters have been changed via API, we must ensure that the grid is refreshed;
    // If ´api.onFilterChanged()´ is not called, the grid will not be refreshed.
    if (gridRef?.current) {
      gridRef.current.getGridApi()?.onFilterChanged();
    }
  }, [selectedRowIds]);

  return (
    <Grid<PricesRow>
      ref={gridRef}
      defaultColDef={columnDefs}
      columnDefs={priceColumns}
      rowData={getAllRows()}
      getRowId={getRowId}
      getContextMenuItems={getContextMenuItems}
      suppressColumnVirtualisation
      isExternalFilterPresent={isExternalFilterPresent}
      doesExternalFilterPass={doesExternalFilterPass}
      onColumnMoved={onColumnMoved}
      onColumnResized={onColumnResized}
      getRowStyle={getRowStyle}
      onCellClicked={onGridCellClicked}
      onContextMenuVisibleChanged={onContextMenuVisibleChange}
      tooltipShowDelay={1000}
      tooltipInteraction={true}
      autoSizeStrategy={undefined}
      domLayout="autoHeight"
    />
  );
}
